import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react"
import { cn } from "@/lib/utils"
import { formatCellValue } from "@/lib/xlsx-format"
import { cellTargetOf, effectiveFillValue } from "@/lib/fill-source"
import type { CellModel, FillProposal, SheetModel } from "@/types/schedule"

// ===================
// Geometry
// ===================

/** Fixed row height — windowing depends on every row being the same height. */
const ROW_HEIGHT = 28
const OVERSCAN_ROWS = 8
/** Used before the container has been measured (first paint, and in jsdom). */
const FALLBACK_VIEWPORT_PX = 640

/** 1-based column index → Excel column letters (1 → 'A', 27 → 'AA'). */
function columnLetter(index: number): string {
  let n = index
  let out = ""
  while (n > 0) {
    const rem = (n - 1) % 26
    out = String.fromCharCode(65 + rem) + out
    n = Math.floor((n - 1) / 26)
  }
  return out
}

/** 'B5' → { col: 2, row: 5 }. Returns null for anything unparseable. */
function parseAddress(address: string): { col: number; row: number } | null {
  const match = /^([A-Z]+)(\d+)$/.exec(address.toUpperCase())
  if (!match) return null
  const [, letters, digits] = match as unknown as [string, string, string]
  let col = 0
  for (const ch of letters) col = col * 26 + (ch.charCodeAt(0) - 64)
  return { col, row: Number(digits) }
}

interface MergeSpan {
  colSpan: number
  rowSpan: number
}

interface MergeRange {
  fromCol: number
  fromRow: number
  toCol: number
  toRow: number
  /** A1 address of the true top-left cell, which owns the content. */
  anchor: string
}

/**
 * Every address a merge covers (anchor included) → the range it belongs to.
 *
 * Keyed by member rather than by anchor because the grid is windowed: a merge
 * whose anchor has scrolled above the window still occupies its columns in
 * every visible row, and rendering it needs the range, not just the anchor.
 * The merge list is the authority over each cell's `mergedAnchor` — a merge can
 * cover addresses the sparse model never emitted.
 */
function buildMerges(merges: string[]): Map<string, MergeRange> {
  const members = new Map<string, MergeRange>()

  for (const range of merges) {
    const [fromRef, toRef] = range.split(":")
    if (!fromRef || !toRef) continue
    const from = parseAddress(fromRef)
    const to = parseAddress(toRef)
    if (!from || !to) continue

    const merge: MergeRange = {
      fromCol: from.col,
      fromRow: from.row,
      toCol: to.col,
      toRow: to.row,
      anchor: `${columnLetter(from.col)}${from.row}`,
    }
    for (let r = from.row; r <= to.row; r++) {
      for (let c = from.col; c <= to.col; c++) {
        members.set(`${columnLetter(c)}${r}`, merge)
      }
    }
  }

  return members
}

/**
 * Where — if anywhere — a merged range renders inside the current window.
 *
 * The anchor is clamped down to `firstRow` when it has scrolled out of view and
 * the rowSpan is clipped at `lastRow`, so the range always occupies its columns
 * in the visible rows (no column shifting under the wrong header) and never
 * stretches the table past the bottom spacer.
 */
function mergePlacement(
  merge: MergeRange,
  col: number,
  row: number,
  firstRow: number,
  lastRow: number
): MergeSpan | null {
  const startRow = Math.max(merge.fromRow, firstRow)
  const endRow = Math.min(merge.toRow, lastRow)
  if (col !== merge.fromCol || row !== startRow || endRow < startRow) return null
  return {
    colSpan: merge.toCol - merge.fromCol + 1,
    rowSpan: endRow - startRow + 1,
  }
}

type FillState = "pending" | "accepted" | "applied" | "dismissed"

function fillState(proposal: FillProposal): FillState | null {
  if (proposal.status === "pending") return "pending"
  if (proposal.status === "dismissed") return "dismissed"
  if (proposal.status === "accepted") return proposal.appliedAt === null ? "accepted" : "applied"
  return null
}

// ===================
// Grid
// ===================

export interface WorkbookGridProps {
  sheet: SheetModel
  proposals: FillProposal[]
  onCellClick: (address: string) => void
  /** Address of the cell whose popover is open, so the grid can mark it. */
  selectedAddress?: string | null
  /**
   * Commit a value the user typed into an untouched cell. Omit to keep the grid
   * read-only (the cells stay inert rather than offering an edit that goes
   * nowhere).
   */
  onCellEdit?: (address: string, value: string) => void | Promise<void>
  /**
   * Values for calculated cells, keyed by address. `verified` ones came from
   * LibreOffice at export and match what the buyer opens; the rest are a live
   * preview of the accepted fills. Cells absent from this map are ones we
   * cannot evaluate honestly — they render as "calculates in Excel".
   */
  calculated?: Map<string, { value: number; verified: boolean }>
}

export interface WorkbookGridHandle {
  /** Bring an A1 address into the window (no-op if it is already there). */
  scrollToAddress: (address: string) => void
}

export const WorkbookGrid = forwardRef<WorkbookGridHandle, WorkbookGridProps>(function WorkbookGrid(
  { sheet, proposals, onCellClick, selectedAddress, onCellEdit, calculated },
  ref
) {
  const containerRef = useRef<HTMLDivElement>(null)
  const [scrollTop, setScrollTop] = useState(0)
  const [viewportPx, setViewportPx] = useState(0)

  const handleScroll = useCallback((event: React.UIEvent<HTMLDivElement>) => {
    setScrollTop(event.currentTarget.scrollTop)
    setViewportPx(event.currentTarget.clientHeight)
  }, [])

  // Measure on mount and on resize. Without this the window stays sized to the
  // fallback until the user's first scroll, leaving a blank band under the last
  // rendered row on a tall display.
  useLayoutEffect(() => {
    const element = containerRef.current
    if (!element) return
    const measure = () => setViewportPx(element.clientHeight)
    measure()
    if (typeof ResizeObserver === "undefined") return
    const observer = new ResizeObserver(measure)
    observer.observe(element)
    return () => observer.disconnect()
  }, [])

  useImperativeHandle(ref, () => ({
    scrollToAddress: (address: string) => {
      const parsed = parseAddress(address)
      if (!parsed) return
      const top = Math.max(0, (parsed.row - 1) * ROW_HEIGHT)
      // Drive the window from state as well as the DOM: a programmatic
      // scrollTop assignment fires no scroll event, so nothing else would tell
      // the component to re-window.
      setScrollTop(top)
      if (containerRef.current) containerRef.current.scrollTop = top
    },
  }), [])

  const cellsByAddress = useMemo(() => {
    const map = new Map<string, CellModel>()
    for (const row of sheet.rows) {
      for (const cell of row.cells) map.set(cell.address, cell)
    }
    return map
  }, [sheet])

  const proposalsByAddress = useMemo(() => {
    const map = new Map<string, FillProposal>()
    for (const proposal of proposals) {
      const cell = cellTargetOf(proposal)
      if (!cell || cell.sheet !== sheet.name) continue
      if (proposal.status === "superseded") continue
      map.set(cell.address, proposal)
    }
    return map
  }, [proposals, sheet.name])

  const mergeMembers = useMemo(() => buildMerges(sheet.merges), [sheet.merges])

  const rowCount = useMemo(
    () => Math.max(sheet.rowCount, ...sheet.rows.map((r) => r.r), 0),
    [sheet]
  )
  const colCount = Math.max(sheet.colCount, 1)

  const visibleRows = Math.ceil((viewportPx || FALLBACK_VIEWPORT_PX) / ROW_HEIGHT)
  const firstRow = Math.max(1, Math.floor(scrollTop / ROW_HEIGHT) + 1 - OVERSCAN_ROWS)
  const lastRow = Math.min(rowCount, firstRow + visibleRows + OVERSCAN_ROWS * 2)

  const rowIndexes: number[] = []
  for (let r = firstRow; r <= lastRow; r++) rowIndexes.push(r)

  const padTop = (firstRow - 1) * ROW_HEIGHT
  const padBottom = Math.max(0, (rowCount - lastRow) * ROW_HEIGHT)

  return (
    <div
      ref={containerRef}
      onScroll={handleScroll}
      className="h-full overflow-auto bg-card"
      data-testid="workbook-grid"
    >
      <table className="w-max border-separate border-spacing-0 text-sm">
        <thead>
          <tr>
            <th
              scope="col"
              className="sticky left-0 top-0 z-20 w-10 border-b border-r border-border bg-muted/50 px-2 text-[11px] font-normal text-muted-foreground"
            >
              <span className="sr-only">Row</span>
            </th>
            {Array.from({ length: colCount }, (_, i) => (
              <th
                key={i}
                scope="col"
                className="sticky top-0 z-10 min-w-[7rem] border-b border-r border-border bg-muted/50 px-2 py-1 text-left text-[11px] font-normal text-muted-foreground"
              >
                {columnLetter(i + 1)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {padTop > 0 && (
            <tr aria-hidden="true">
              <td colSpan={colCount + 1} style={{ height: padTop }} />
            </tr>
          )}

          {rowIndexes.map((r) => (
            <tr key={r} style={{ height: ROW_HEIGHT }}>
              <th
                scope="row"
                className="sticky left-0 z-10 border-b border-r border-border bg-muted/50 px-2 text-right text-[11px] font-normal text-muted-foreground"
              >
                {r}
              </th>
              {Array.from({ length: colCount }, (_, i) => {
                const address = `${columnLetter(i + 1)}${r}`
                const merge = mergeMembers.get(address)
                let span: MergeSpan | undefined
                let contentAddress = address

                if (merge) {
                  const placement = mergePlacement(merge, i + 1, r, firstRow, lastRow)
                  if (!placement) return null
                  span = placement
                  // The clamped cell still shows — and is addressed as — the
                  // true anchor, so proposal lookups and the popover agree.
                  contentAddress = merge.anchor
                }

                return (
                  <GridCell
                    key={address}
                    address={contentAddress}
                    cell={cellsByAddress.get(contentAddress)}
                    proposal={proposalsByAddress.get(contentAddress)}
                    span={span}
                    isSelected={selectedAddress === contentAddress}
                    onCellClick={onCellClick}
                    onCellEdit={onCellEdit}
                    calculated={calculated?.get(address)}
                  />
                )
              })}
            </tr>
          ))}

          {padBottom > 0 && (
            <tr aria-hidden="true">
              <td colSpan={colCount + 1} style={{ height: padBottom }} />
            </tr>
          )}
        </tbody>
      </table>
    </div>
  )
})

// ===================
// Cell
// ===================

/**
 * Can the user type into this cell?
 *
 * Mirrors the apply layer's refusals (`validateSetValueOp`) so the grid never
 * offers an edit the server would reject: formulas calculate themselves, locked
 * cells belong to the buyer, and a merged range is written through its anchor.
 * The server re-checks regardless — this is the affordance, not the guard.
 */
export function isCellEditable(cell: CellModel | undefined): boolean {
  // Non-anchor merge members never render, so they cannot be reached here.
  if (!cell) return true // absent from the sparse model = empty and writable
  return !cell.isFormula && !cell.isProtected
}

/** Tooltip for a calculated cell: what it computes, and how sure we are. */
function calculatedTitle(
  formula: string | undefined,
  calculated: { value: number; verified: boolean } | undefined
): string {
  const head = formula ? `=${formula}` : 'Calculated by the workbook'
  if (!calculated) {
    return `${head} — calculates in Excel when the file opens.`
  }
  return calculated.verified
    ? `${head} — calculated by the workbook.`
    : `${head} — preview of your accepted fills. Confirmed when you export.`
}

interface GridCellProps {
  address: string
  cell: CellModel | undefined
  proposal: FillProposal | undefined
  span: MergeSpan | undefined
  isSelected: boolean
  onCellClick: (address: string) => void
  onCellEdit?: ((address: string, value: string) => void | Promise<void>) | undefined
  calculated?: { value: number; verified: boolean } | undefined
}

function GridCell({
  address,
  cell,
  proposal,
  span,
  isSelected,
  onCellClick,
  onCellEdit,
  calculated,
}: GridCellProps) {
  const state = proposal ? fillState(proposal) : null
  const isFill = state === "pending" || state === "accepted" || state === "applied"
  const original = formatCellValue(cell?.value ?? null, cell?.numFmt)
  const display = isFill && proposal ? effectiveFillValue(proposal) : original
  const hasApplyError = Boolean(proposal?.applyError)

  // A dismissed fill is not a live fill: the user rejected it, so the cell is
  // back to being empty and typing their own value is the obvious next move.
  // Keying on `proposal` alone left those cells inert AND still advertising a
  // review that has nothing to show.
  const hasLiveFill = isFill
  const editable = Boolean(onCellEdit) && !hasLiveFill && isCellEditable(cell)
  const [isEditing, setIsEditing] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (isEditing) inputRef.current?.focus()
  }, [isEditing])

  const commit = (raw: string) => {
    setIsEditing(false)
    const next = raw.trim()
    // Unchanged or still empty: nothing to stage.
    if (next.length === 0 || next === original) return
    void onCellEdit?.(address, next)
  }

  return (
    <td
      data-cell-address={address}
      {...(state ? { "data-fill-state": state } : {})}
      colSpan={span?.colSpan ?? 1}
      rowSpan={span?.rowSpan ?? 1}
      className={cn(
        "max-w-[18rem] truncate border-b border-r border-border px-2 align-middle",
        cell?.styleHints?.bold && "font-medium",
        cell?.isFormula && "text-muted-foreground",
        // Only cells still awaiting a decision are tinted. The accepted wash was
        // 5% opacity from pending — indistinguishable in practice, so the two
        // states leaned on the dot anyway. One tint + the dot reads cleaner.
        state === "pending" && "bg-[hsl(var(--brand)/0.08)]",
        hasApplyError && "bg-destructive/10",
        isSelected && "ring-2 ring-inset ring-[hsl(var(--brand))]"
      )}
    >
      {hasLiveFill ? (
        <button
          type="button"
          onClick={() => onCellClick(address)}
          className="flex h-full w-full items-center gap-1.5 truncate text-left"
          title={`Review the fill for ${address}`}
        >
          {state === "pending" && (
            <span
              aria-hidden="true"
              className="h-1.5 w-1.5 shrink-0 rounded-full bg-[hsl(var(--brand))]"
            />
          )}
          <span className="truncate">{display}</span>
        </button>
      ) : isEditing ? (
        <input
          ref={inputRef}
          type="text"
          defaultValue={original}
          aria-label={`Value for ${address}`}
          className="h-full w-full bg-transparent text-sm outline-none"
          onBlur={(e) => commit(e.currentTarget.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault()
              commit(e.currentTarget.value)
            }
            if (e.key === "Escape") {
              e.preventDefault()
              setIsEditing(false)
            }
          }}
        />
      ) : cell?.isFormula ? (
        // A calculated cell used to render as an unexplained blank, which reads
        // as "the AI missed this". Show the number when we have one, always say
        // it is calculated, and show the formula behind it.
        <button
          type="button"
          className="flex h-full min-h-[1.25rem] w-full cursor-help items-center gap-1 truncate text-left"
          title={calculatedTitle(cell.formula, calculated)}
          aria-label={calculatedTitle(cell.formula, calculated)}
        >
          <span aria-hidden="true" className="shrink-0 text-[10px] text-muted-foreground">
            ƒ
          </span>
          <span
            className={cn(
              "truncate",
              calculated && !calculated.verified && "decoration-dotted underline-offset-2 underline"
            )}
          >
            {calculated
              ? formatCellValue(calculated.value, cell.numFmt)
              : (display ?? "")}
          </span>
        </button>
      ) : editable ? (
        // Typing into an untouched cell is the last mile of finishing a schedule.
        // It still goes through the proposal pipeline server-side, so the workbook
        // is never written directly.
        <button
          type="button"
          onClick={() => setIsEditing(true)}
          // min-h: an empty cell has no text to give the button height, and an
          // empty cell is exactly the one a user wants to type into — without
          // this the click target collapses to 0px.
          className="flex h-full min-h-[1.25rem] w-full items-center truncate text-left"
          title={`Edit ${address}`}
        >
          <span className="truncate">{display}</span>
        </button>
      ) : (
        display
      )}
    </td>
  )
}
